import { AimsCommonTableHeader, AimsCommonTableHeaderType } from '@aims/common-components';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, startWith, take } from 'rxjs/operators';

import { AimsCommonPopupEditorData } from '@aims/common-components';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CacheItem, TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';

@Component({
  selector: 'app-manage-users-popup',
  templateUrl: './manage-users-popup.component.html',
  styleUrls: ['./manage-users-popup.component.css']
})
export class ManageUsersPopupComponent implements OnInit {
  constructor(
    contentData: AimsCommonPopupEditorData,
    public authService: AuthService,
    private transferHttp: TransferHttpService){
      this.isNew = contentData.isNew;
      this.userToEdit = contentData.contentData;
      this.formGroup = contentData.formGroup;
      //Regex requiring at least one special character, one upper, one lower, one number and more than 8 characters, no spaces https://regex101.com/r/Z3REM0/1
      const validPasswordPattern = /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[^\w\d\s:])([^\s]){8,100}$/;

      //set form controls indivually because this modifies the original form group
      this.formGroup.addControl('userName', new FormControl(this.userToEdit.userName, Validators.required));
      this.formGroup.addControl('userId', new FormControl(this.userToEdit.userId));
      this.formGroup.addControl('tenantId', new FormControl(this.userToEdit.tenantId, Validators.required));
      this.formGroup.addControl('email', new FormControl(this.userToEdit.email, Validators.required));
      this.formGroup.addControl('employeeId', new FormControl(this.userToEdit.employeeId, Validators.required));
      this.formGroup.addControl('generatePwd', new FormControl(false, {updateOn: 'blur'}));
      this.formGroup.addControl('setPwd', new FormControl(this.isNew, {updateOn: 'blur'}));
      this.formGroup.addControl('password', new FormControl({ value: '', disabled: !this.isNew}, {
        validators: [
          Validators.pattern(validPasswordPattern)
        ]
      }
      ));
      this.formGroup.addControl('confirmPassword', new FormControl({ value: '', disabled: !this.isNew}, {
        validators: [
          Validators.pattern(validPasswordPattern)
        ]
      }
      ));

      if(!this.isNew){
        this.formGroup.controls.userName.disable();
      }

      this.formGroup.get('confirmPassword').valueChanges.subscribe((v:string)=> {
        this.validatePasswords(v);
      });
      this.formGroup.get('password').valueChanges.subscribe((v:string)=> {
        this.validatePasswords(v);
      });
      this.formGroup.get('generatePwd').valueChanges.subscribe((v: Boolean) => {
        this.validatePasswords(v);
      });
      this.formGroup.get('userName').valueChanges.subscribe((v:string) => {
        this.validateUserName(v);
      })
  }

  validateUserName(userName){
    const isValid = ["\\", "@"].some(s => (userName) && userName.length > 1 ? userName.indexOf(s) !== -1 : false);
    if(isValid){
      this.formGroup.controls.userName.setErrors({invalid: true});
    }else{
      this.formGroup.controls.userName.setErrors(null);
    }
  }

  handleSetPwdCheckboxChange(e) {
    if (e.checked || this.isNew) {
      this.formGroup.patchValue({
        setPwd: e.checked,
        generatePwd: !e.checked
      });
    }
    else {
      this.formGroup.patchValue({
        setPwd: e.checked,
      });
    }

    if (e.checked) {
      this.formGroup.controls.password.enable();
      this.formGroup.controls.confirmPassword.enable();
    }
    else {
      this.formGroup.controls.password.disable();
      this.formGroup.controls.confirmPassword.disable();
    }

    this.validatePasswords("");
  }

  handleGeneratePwdCheckboxChange(e){
    if (e.checked || this.isNew) {
      this.formGroup.patchValue({
        generatePwd: e.checked,
        setPwd: !e.checked,
      });
    }
    else {
      this.formGroup.patchValue({
        generatePwd: e.checked,
      });
    }

    if (e.checked) {
      this.formGroup.controls.password.disable();
      this.formGroup.controls.confirmPassword.disable();
    }
    else if (this.isNew) {
      this.formGroup.controls.password.enable();
      this.formGroup.controls.confirmPassword.enable();
    }
    this.validatePasswords("");
  }

  validatePasswords(password){
    //validate passwords are the same, if generatePwd these rules are overridden
    password = this.formGroup.controls.password.value;
    const confirmPassword = this.formGroup.controls.confirmPassword.value;
    const generatePwd = this.formGroup.controls.generatePwd.value;

    if ((password === confirmPassword &&
      password.length > 0 && confirmPassword.length > 0 &&
      this.formGroup.controls.password.valid) ||
      generatePwd == true)
    {
      this.formGroup.controls.password.setErrors(null);
      this.formGroup.controls.confirmPassword.setErrors(null);
    } else if(!this.formGroup.controls.password.valid && password === confirmPassword ) {
      //confirm technically matches, so no error
      this.formGroup.controls.confirmPassword.setErrors(null);
    }
    else{
      this.formGroup.controls.confirmPassword.setErrors({ mismatch: true });
    }
  }

  employeeLookupChange(employeeId){
    this.formGroup.patchValue({
      employeeId: employeeId
    });
    if(!employeeId){
      this.isEmployeeLookupRequired = true;
      this.formGroup.controls.employeeId.setErrors({invalid: true})
    }else{
      this.isEmployeeLookupRequired = false;
      this.formGroup.controls.employeeId.setErrors(null);
    }

  }

  ngOnInit() {
    //show it as a required field

    this.formGroup.controls.password.setErrors({invalid: true});
    this.formGroup.controls.confirmPassword.setErrors({invalid: true});
    this.isEmployeeLookupRequired = this.userToEdit.employeeId == null || this.userToEdit.employeeId == 0;
  }
  ngOnDestroy(){
    this.formGroup.valueChanges.unsubscribe();
  }
  userToEdit;
  header: string;
  isNew:boolean = false;
  public formGroup: any;
  hidePassword = true;
  hideConfirmPassword = true;
  disablePwd = false;
  isEmployeeLookupRequired = false;
  isAimsAdmin:boolean = this.authService.isAimsAdmin;
  tenants = this.authService.getTenants();
  employees = this.transferHttp.getCache(CacheItem.allEmployees);

  userValidationMessages = {
    'userName': [
      {type: 'invalid', message: 'Your username cannot not have domain\\ or @companyname.com'}
    ],
    'password': [
      { type: 'pattern', message: 'Your password must be 8 characters long, must contain at least one uppercase, one lowercase, one number and one special character' }
    ],
    'confirmPassword': [
      { type: 'pattern', message: 'Your password must be 8 characters long, contain at least one uppercase, one lowercase, one number and one special character'},
      { type: 'mismatch', message: 'Your passwords do not match' }
    ]
  }

  employeeColumns:AimsCommonTableHeader[] = [
    { key: 'employeeNumber', label: 'Employee Number', type: AimsCommonTableHeaderType.STRING },
    { key: 'fullName', label: 'Name', type: AimsCommonTableHeaderType.STRING }
  ];
}
